import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ListMusic, 
  ArrowRight, 
  ArrowLeft, 
  X, 
  Music, 
  Users, 
  Smartphone, 
  CheckCircle,
  Play,
  Eye,
  EyeOff
} from 'lucide-react';
import { ysr2Mobile } from '../styles/mobileDesignSystem';

/** Above AdminDashboard mobile nav (z-[100001]); below Melody (2147483645). */
const SETLIST_WIZARD_Z = 2147483644;

const SetlistWizard = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showDemo, setShowDemo] = useState(false);
  const [demoStep, setDemoStep] = useState(0);

  console.log('🔍 SetlistWizard: Rendering with isOpen:', isOpen);

  const steps = [
    {
      title: "What are Setlists?",
      content: "Setlists help you organize songs and display your selected Setlist to your audience. Create themed Setlists like \"Wedding Songs\", \"Pub Classics\", or \"Acoustic Set\" to quickly access and showcase your most popular songs!",
      icon: ListMusic,
      color: "orange"
    },
    {
      title: "Visual Demonstration",
      content: "Let's see how setlists work in practice! Watch how switching from 'All Songs' to a specific setlist changes what your audience sees.",
      icon: Eye,
      color: "blue"
    }
  ];

  const demoSteps = [
    {
      title: "All Songs (Default)",
      description: "Your audience sees all your available songs",
      songs: [
        { title: "Bohemian Rhapsody", artist: "Queen", price: "£8" },
        { title: "Sweet Child O' Mine", artist: "Guns N' Roses", price: "£7" },
        { title: "Hotel California", artist: "Eagles", price: "£6" },
        { title: "Wonderwall", artist: "Oasis", price: "£5" },
        { title: "Don't Stop Believin'", artist: "Journey", price: "£6" }
      ],
      isAllSongs: true
    },
    {
      title: "Acoustic Songs Setlist",
      description: "Your audience now only sees songs from your Acoustic Songs setlist",
      songs: [
        { title: "More Than Words", artist: "Extreme", price: "£4" },
        { title: "Good Riddance (Time of your life)", artist: "Green Day", price: "£5" },
        { title: "All About You", artist: "McFly", price: "£4" }
      ],
      isAllSongs: false
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      if (newStep === 1) {
        setShowDemo(true);
        setDemoStep(0);
      }
    } else {
      onComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      if (newStep === 1) {
        setShowDemo(true);
        setDemoStep(0);
      } else {
        setShowDemo(false);
      }
    }
  };

  const nextDemoStep = () => {
    if (demoStep < demoSteps.length - 1) {
      setDemoStep(demoStep + 1);
    } else {
      setShowDemo(false);
      nextStep();
    }
  };

  const getColorClasses = (color) => {
    const colorMap = {
      orange: {
        bg: 'bg-orange-900/20',
        border: 'border-orange-500/40',
        text: 'text-orange-400',
        button: 'bg-orange-600 hover:bg-orange-700'
      },
      blue: {
        bg: 'bg-blue-900/20',
        border: 'border-blue-500/40',
        text: 'text-blue-400',
        button: 'bg-blue-600 hover:bg-blue-700'
      }
    };
    return colorMap[color] || colorMap.orange;
  };

  if (!isOpen) {
    console.log('🚫 SetlistWizard: Not open, returning null');
    return null;
  }
  
  console.log('✅ SetlistWizard: Open, rendering component');

  const currentStepData = steps[currentStep];
  const colors = getColorClasses(currentStepData.color);
  const IconComponent = currentStepData.icon;

  return (
    <motion.div
      className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 touch-manipulation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ zIndex: SETLIST_WIZARD_Z }}
    >
      <motion.div
        className={`${ysr2Mobile.surfaceStrong} p-0 max-w-2xl w-full flex flex-col min-h-0 max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-bottom)-1rem))] overflow-hidden shadow-2xl border border-white/10`}
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        transition={{ type: 'spring', damping: 26, stiffness: 320 }}
      >
        {/* Top: title + progress (always visible above scroll) */}
        <div className="flex-shrink-0 px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-white/10 bg-slate-950/40">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center space-x-3 min-w-0">
              <div className={`w-11 h-11 sm:w-12 sm:h-12 shrink-0 ${colors.bg} ${colors.border} border rounded-xl flex items-center justify-center`}>
                <IconComponent className={`w-5 h-5 sm:w-6 sm:h-6 ${colors.text}`} />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-white leading-tight">{currentStepData.title}</h2>
                <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
                  Step {currentStep + 1} of {steps.length}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 w-10 h-10 bg-gray-700/90 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors touch-manipulation"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-gray-200" />
            </button>
          </div>
          <div className="w-full bg-gray-700/80 rounded-full h-2 mt-3">
            <motion.div
              className={`h-2 rounded-full ${colors.button}`}
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Middle: scrolls; footer stays out of this region */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 space-y-4">
          <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{currentStepData.content}</p>

          {currentStep === 1 && showDemo && (
            <motion.div
              className="bg-gray-800/50 border border-gray-600 rounded-xl p-4 sm:p-6"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <div className="text-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2">{demoSteps[demoStep].title}</h3>
                <p className="text-xs sm:text-sm text-gray-400">{demoSteps[demoStep].description}</p>

                {demoStep === 0 && (
                  <motion.button
                    type="button"
                    onClick={nextDemoStep}
                    className="mt-3 w-full max-w-sm mx-auto px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded-xl transition-colors touch-manipulation font-semibold"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Switch to Acoustic Songs Setlist →
                  </motion.button>
                )}
              </div>

              <div className="bg-gray-900 border border-gray-600 rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <div className="flex items-center space-x-2 min-w-0">
                    <Smartphone className="w-4 h-4 text-blue-400 shrink-0" />
                    <span className="text-sm font-medium text-white truncate">Audience View</span>
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                    <Users className="w-4 h-4 text-green-400" />
                    <span className="text-xs text-gray-400">Live</span>
                  </div>
                </div>

                <motion.div
                  className="space-y-2"
                  key={demoStep}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                >
                  {demoSteps[demoStep].songs.map((song, index) => (
                    <motion.div
                      key={`${demoStep}-${song.title}`}
                      className="flex items-center justify-between gap-2 p-2.5 sm:p-3 bg-gray-700 rounded-lg"
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.08 }}
                    >
                      <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                        <Music className="w-4 h-4 text-purple-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-white text-xs sm:text-sm font-medium leading-snug break-words">{song.title}</div>
                          <div className="text-gray-400 text-[11px] sm:text-xs">by {song.artist}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1.5 sm:space-x-2 shrink-0">
                        <span className="text-[11px] sm:text-xs text-gray-400">{song.price}</span>
                        <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center pointer-events-none">
                          <Play className="w-3 h-3 text-white" />
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                <div className="mt-3 pt-3 border-t border-gray-600">
                  <motion.div
                    className="flex items-center justify-center flex-wrap gap-x-2 gap-y-1 text-center"
                    key={demoStep}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ListMusic className="w-4 h-4 text-orange-400 shrink-0" />
                    <span className="text-xs text-gray-400">
                      {demoSteps[demoStep].isAllSongs ? 'All Songs' : 'Acoustic Songs Setlist'}
                    </span>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Bottom: always visible; safe area; wraps on narrow widths */}
        <div className="flex-shrink-0 border-t border-gray-600/80 bg-slate-950/95 backdrop-blur-md px-3 sm:px-6 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-stretch sm:justify-between">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-colors w-full sm:w-auto sm:min-w-[8.5rem] touch-manipulation ${
                currentStep === 0
                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700'
                  : 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600'
              }`}
            >
              <ArrowLeft className="w-4 h-4 shrink-0" />
              <span>Previous</span>
            </button>

            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:flex-1 sm:justify-end sm:min-w-0">
              {currentStep === 1 && showDemo ? (
                <button
                  type="button"
                  onClick={nextDemoStep}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white transition-colors w-full sm:flex-1 sm:max-w-[14rem] font-semibold text-sm touch-manipulation ${colors.button}`}
                >
                  <span className="text-center">{demoStep < demoSteps.length - 1 ? 'Next' : 'Continue'}</span>
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={nextStep}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white transition-colors w-full sm:flex-1 sm:max-w-[14rem] font-semibold text-sm touch-manipulation ${colors.button}`}
                >
                  <span className="text-center">{currentStep === steps.length - 1 ? 'Create Setlist' : 'Next'}</span>
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SetlistWizard;
